import { useEffect, useState, useRef } from "react";
import { API, axios } from "@/api";
import { Trophy, Star, Target, Award, Calendar, Users, Medal, TrendingUp, X } from "lucide-react";

const MILESTONE_ICONS = {
  go_live: Target, birthday_offer: Award, punch_card: Star, photos_verified: Star,
  schedule_kept: Calendar, community_spotted: Users, birthday_crawl_listed: Trophy,
  follower_growth: Users, punch_completions: Medal, profile_complete: TrendingUp,
};

const MILESTONE_LABELS = {
  go_live: "Go Live", birthday_offer: "Birthday Offer", punch_card: "Punch Card",
  photos_verified: "Photos Verified", schedule_kept: "Schedule Kept",
  community_spotted: "Community Spotted", birthday_crawl_listed: "Birthday Crawl Listed",
  follower_growth: "Follower Growth", punch_completions: "Punch Completions",
  profile_complete: "Profile Complete",
};

const MILESTONE_REASONS = {
  go_live: { 1: "You went live for the first time. Your followers can now find you.", 2: "You have gone live 10 times. Your followers have seen you in action. Keep going.", 3: "50 live sessions. You are a regular presence." },
  birthday_offer: { 1: "You set up your birthday offer. People celebrating nearby will see it.", 2: "Your first birthday claim was confirmed. Real customers found you.", 3: "10 birthday claims confirmed. Your offer is driving real foot traffic." },
  punch_card: { 1: "Punch card configured. Repeat customers have a reason to come back.", 2: "First punch recorded. A customer started their loyalty journey.", 3: "First punch card completed. A customer earned their reward." },
  photos_verified: { 1: "Truck exterior photo verified. Customers can recognize you.", 2: "Menu board photo verified. People see your menu before arriving.", 3: "Both photos verified. Your listing looks professional." },
  schedule_kept: { 1: "You went live on a scheduled day. Reliability builds trust.", 2: "5 days in a row on schedule.", 3: "10 days in a row. Impressive consistency." },
  community_spotted: { 1: "Your first community spot verified.", 2: "10 verified spots this month.", 3: "50 lifetime verified spots." },
  birthday_crawl_listed: { 1: "You appeared in a birthday crawl.", 2: "Listed in 10 crawls.", 3: "50 crawls. You are a destination." },
  follower_growth: { 1: "10 followers.", 2: "50 followers. Real audience.", 3: "100 followers. A community." },
  punch_completions: { 1: "First punch card completed.", 2: "10 completions.", 3: "50 completions. Proven driver." },
  profile_complete: { 1: "Profile is 50% complete.", 2: "Profile is 80% complete.", 3: "Profile is 100% complete." },
};

function ConfettiPiece({ delay, left }) {
  const colors = ["#F59E0B", "#22C55E", "#3B82F6", "#EF4444", "#A855F7", "#EC4899"];
  const color = colors[Math.floor(Math.random() * colors.length)];
  return (
    <div
      className="absolute w-2 h-2 rounded-sm animate-bounce"
      style={{
        left: `${left}%`,
        top: "-10px",
        backgroundColor: color,
        animationDelay: `${delay}ms`,
        animationDuration: `${1500 + Math.random() * 2000}ms`,
        opacity: 0.8,
      }}
    />
  );
}

export default function MilestoneCelebration({ onDismiss }) {
  const [celebration, setCelebration] = useState(null);
  const [show, setShow] = useState(false);
  const [confettiVisible, setConfettiVisible] = useState(true);
  const dismissed = useRef(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/vendor/pending-celebration`);
        if (res.data?.pending_celebration) {
          setCelebration(res.data.pending_celebration);
          setShow(true);
          setTimeout(() => setConfettiVisible(false), 5000);
        }
      } catch {}
    })();
  }, []);

  const handleDismiss = async () => {
    if (dismissed.current) return;
    dismissed.current = true;
    try {
      await axios.post(`${API}/vendor/clear-celebration`);
    } catch {}
    setShow(false);
    onDismiss?.();
  };

  if (!show || !celebration) return null;

  const key = celebration.milestone_key;
  const tier = celebration.tier;
  const Icon = MILESTONE_ICONS[key] || Trophy;
  const label = MILESTONE_LABELS[key] || key;
  const reason = MILESTONE_REASONS[key]?.[tier] || `You reached tier ${tier} for ${label}.`;

  return (
    <div className="fixed inset-0 z-[200] bg-black/80 backdrop-blur-sm flex items-center justify-center px-4" data-testid="milestone-celebration-modal">
      {/* Confetti */}
      {confettiVisible && (
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {Array.from({ length: 40 }).map((_, i) => (
            <ConfettiPiece key={i} delay={i * 80} left={Math.random() * 100} />
          ))}
        </div>
      )}

      <div className="bg-[#2C2C2E] rounded-2xl p-8 max-w-sm w-full text-center relative animate-in fade-in zoom-in duration-500">
        <div className="w-20 h-20 mx-auto mb-4 bg-[#F59E0B]/10 rounded-full flex items-center justify-center">
          <Icon className="w-10 h-10 text-[#F59E0B]" />
        </div>

        <h2 className="text-xl font-bold text-white mb-1" data-testid="celebration-title">
          {label} — Tier {tier}
        </h2>

        <p className="text-sm text-white/70 mb-4" data-testid="celebration-reason">{reason}</p>

        <p className="text-xs text-white/40 mb-6">
          Your {label.toLowerCase()} badge is now on your public profile.
        </p>

        <button onClick={handleDismiss}
          className="w-full bg-[#F59E0B] text-black font-semibold rounded-xl py-3 text-sm"
          data-testid="celebration-dismiss-btn">
          Done
        </button>
      </div>
    </div>
  );
}
